import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/layout/DashboardLayout";
import MobileLayout from "@/pages/mobile/MobileLayout";
import MobileAdminRoute from "@/pages/mobile/MobileAdminRoute";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const LeadsPage = lazy(() => import("@/pages/LeadsPage"));
const BrandsPage = lazy(() => import("@/pages/BrandsPage"));
const ModelsPage = lazy(() => import("@/pages/ModelsPage"));
const BranchesPage = lazy(() => import("@/pages/BranchesPage"));
const CampaignsPage = lazy(() => import("@/pages/CampaignsPage"));
const ConversationsPage = lazy(() => import("@/pages/ConversationsPage"));
const ChatbotTesterPage = lazy(() => import("@/pages/ChatbotTesterPage"));
const ChatbotContentPage = lazy(() => import("@/pages/ChatbotContentPage"));
const BotSettingsPage = lazy(() => import("@/pages/BotSettingsPage"));
const PipelineSettingsPage = lazy(() => import("@/pages/PipelineSettingsPage"));
const PipelineLogsPage = lazy(() => import("@/pages/PipelineLogsPage"));
const BroadcastPage = lazy(() => import("@/pages/BroadcastPage"));
const DepartmentsPage = lazy(() => import("@/pages/DepartmentsPage"));
const AppointmentsPage = lazy(() => import("@/pages/AppointmentsPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const HelpPage = lazy(() => import("@/pages/HelpPage"));
const ComplaintsPage = lazy(() => import("@/pages/ComplaintsPage"));
const NpsPage = lazy(() => import("@/pages/NpsPage"));
const GiftsPage = lazy(() => import("@/pages/GiftsPage"));
const ScoreboardPage = lazy(() => import("@/pages/ScoreboardPage"));
const PublicNpsPage = lazy(() => import("@/pages/PublicNpsPage"));
const PublicBookingPage = lazy(() => import("@/pages/PublicBookingPage"));
const MobileHomePage = lazy(() => import("@/pages/mobile/MobileHomePage"));
const MobileLeadsPage = lazy(() => import("@/pages/mobile/MobileLeadsPage"));
const MobileLeadDetailPage = lazy(() => import("@/pages/mobile/MobileLeadDetailPage"));
const MobileNotificationsPage = lazy(() => import("@/pages/mobile/MobileNotificationsPage"));
const MobileProfilePage = lazy(() => import("@/pages/mobile/MobileProfilePage"));
const MobileConversationsPage = lazy(() => import("@/pages/mobile/MobileConversationsPage"));
const MobileConversationDetailPage = lazy(() => import("@/pages/mobile/MobileConversationDetailPage"));
const MobileTeamPage = lazy(() => import("@/pages/mobile/MobileTeamPage"));
const MobileComplaintsPage = lazy(() => import("@/pages/mobile/MobileComplaintsPage"));
const MobileBroadcastPage = lazy(() => import("@/pages/mobile/MobileBroadcastPage"));
const MobileBroadcastDetailPage = lazy(() => import("@/pages/mobile/MobileBroadcastDetailPage"));
const MobileKnowledgePage = lazy(() => import("@/pages/mobile/MobileKnowledgePage"));
const CustomersPage = lazy(() => import("@/pages/CustomersPage"));
const CustomerDetailPage = lazy(() => import("@/pages/CustomerDetailPage"));
const MobileCustomersPage = lazy(() => import("@/pages/mobile/MobileCustomersPage"));
const MobileCustomerDetailPage = lazy(() => import("@/pages/mobile/MobileCustomerDetailPage"));

const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center" data-testid="page-loader">
    <div className="w-7 h-7 border-[3px] border-red-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

const adminOnly = (el) => <MobileAdminRoute>{el}</MobileAdminRoute>;

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/online-randevu" element={<PublicBookingPage />} />
        <Route path="/anket/:token" element={<PublicNpsPage />} />
        <Route path="/m" element={
          <ProtectedRoute>
            <MobileLayout />
          </ProtectedRoute>
        }>
          <Route index element={<MobileHomePage />} />
          <Route path="leads" element={<MobileLeadsPage />} />
          <Route path="leads/:leadId" element={<MobileLeadDetailPage />} />
          <Route path="notifications" element={<MobileNotificationsPage />} />
          <Route path="customers" element={<MobileCustomersPage />} />
          <Route path="customers/:customerId" element={<MobileCustomerDetailPage />} />
          <Route path="profile" element={<MobileProfilePage />} />
          <Route path="conversations" element={adminOnly(<MobileConversationsPage />)} />
          <Route path="conversations/:conversationId" element={adminOnly(<MobileConversationDetailPage />)} />
          <Route path="team" element={adminOnly(<MobileTeamPage />)} />
          <Route path="complaints" element={adminOnly(<MobileComplaintsPage />)} />
          <Route path="broadcast" element={adminOnly(<MobileBroadcastPage />)} />
          <Route path="broadcast/:broadcastId" element={adminOnly(<MobileBroadcastDetailPage />)} />
          <Route path="knowledge" element={adminOnly(<MobileKnowledgePage />)} />
        </Route>
        <Route path="/" element={
          <ProtectedRoute adminOnly>
            <DashboardLayout />
          </ProtectedRoute>
        }>
          <Route index element={<DashboardPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/:customerId" element={<CustomerDetailPage />} />
          <Route path="brands" element={<BrandsPage />} />
          <Route path="models" element={<ModelsPage />} />
          <Route path="branches" element={<BranchesPage />} />
          <Route path="campaigns" element={<CampaignsPage />} />
          <Route path="conversations" element={<ConversationsPage />} />
          <Route path="chatbot" element={<ChatbotTesterPage />} />
          <Route path="chatbot-content" element={<ChatbotContentPage />} />
          <Route path="bot-settings" element={<BotSettingsPage />} />
          <Route path="pipeline-settings" element={<PipelineSettingsPage />} />
          <Route path="pipeline-logs" element={<PipelineLogsPage />} />
          <Route path="broadcast" element={<BroadcastPage />} />
          <Route path="departments" element={<DepartmentsPage />} />
          <Route path="appointments" element={<AppointmentsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="complaints" element={<ComplaintsPage />} />
          <Route path="nps" element={<NpsPage />} />
          <Route path="gifts" element={<GiftsPage />} />
          <Route path="scoreboard" element={<ScoreboardPage />} />
          <Route path="help" element={<HelpPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

export default App;
